/**
 * useDeviceSelection Hook
 * Phase 1 Day 3 - Device selection and navigation management
 *
 * Manages selected device state with synchronized ref for WebSocket handlers.
 * This hook encapsulates device selection logic that was previously scattered
 * throughout AppContent.js.
 *
 * @returns {Object} Device selection state and actions
 */

import { useEffect, useRef } from "react";
import { useDevicesStore } from "../stores";
import logger from "../utils/logger";

export const useDeviceSelection = () => {
  // Get device selection state from DevicesStore
  const selectedDeviceId = useDevicesStore((state) => state.selectedDeviceId);
  const setSelectedDeviceId = useDevicesStore(
    (state) => state.setSelectedDeviceId
  );

  // Ref to track selectedDeviceId for WebSocket handlers and callbacks
  // This ref is needed because WebSocket message handlers capture stale closures
  const selectedDeviceIdRef = useRef("");

  // Sync ref with state changes
  useEffect(() => {
    selectedDeviceIdRef.current = selectedDeviceId;
    logger.log(
      `[useDeviceSelection] Selected device changed: ${
        selectedDeviceId || "none"
      }`
    );
  }, [selectedDeviceId]);

  /**
   * Select a device by ID
   * @param {string} deviceId - The device ID to select
   */
  const selectDevice = (deviceId) => {
    logger.log(`[useDeviceSelection] Selecting device: ${deviceId}`);
    setSelectedDeviceId(deviceId);
  };

  /**
   * Clear device selection
   */
  const clearSelection = () => {
    logger.log("[useDeviceSelection] Clearing device selection");
    setSelectedDeviceId("");
  };

  /**
   * Check if a device is currently selected
   * @returns {boolean} True if a device is selected
   */
  const hasSelection = () => {
    return Boolean(selectedDeviceId);
  };

  /**
   * Get the currently selected device ID from ref (for closures)
   * @returns {string} The selected device ID
   */
  const getSelectedDeviceId = () => {
    return selectedDeviceIdRef.current;
  };

  return {
    // State
    selectedDeviceId,
    selectedDeviceIdRef,

    // Actions
    selectDevice,
    setSelectedDeviceId, // Backward compatibility
    clearSelection,
    hasSelection,
    getSelectedDeviceId,
  };
};
